import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { fbAuth } from "../lib/firebase";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [fbUser, setFbUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    return onAuthStateChanged(fbAuth, setFbUser);
  }, [checkAuth]);

  // Verifies the Firebase ID token on the backend and establishes our session cookie
  const exchangeFirebase = useCallback(async (firebaseUser, profile = null, forceRefresh = false) => {
    const idToken = await firebaseUser.getIdToken(forceRefresh);
    const { data } = await api.post("/auth/firebase", { id_token: idToken, ...(profile ? { profile } : {}) });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(fbAuth);
    } catch (e) {
      // Firebase sign-out failure shouldn't block backend logout
      console.debug("auth: firebase sign-out failed", e);
    }
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  }, []);

  const value = useMemo(
    () => ({ user, setUser, fbUser, loading, logout, checkAuth, exchangeFirebase }),
    [user, fbUser, loading, logout, checkAuth, exchangeFirebase]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
